import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { ModalContext } from 'context/ModalContext';
import Appointment from 'components/widgets/modals/AddAppointment';
import ViewAppointment from 'components/widgets/modals/ViewAppointmentDetails';
import { formatDate } from '@fullcalendar/common';


// ──────────────────────────────────────────────────────────────
// Styled Components – Enhanced for Grid & Cards
// ──────────────────────────────────────────────────────────────
const EventCard = styled.div`
  background: #2c3e50;
  border-radius: 10px;
  padding: 1rem;
  color: #f2f2f2;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: default;
  min-height: 120px;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`;

const EventDate = styled.div`
  font-weight: 600;
  font-size: 0.9em;
  margin-bottom: 0.5rem;
  color: #a8dadc;
`;

const EventTitle = styled.div`
  font-size: 1em;
  font-weight: 500;
  margin-bottom: 0.75rem;
  color: #f1faee;
`;

const EventDetails = styled.div`
  font-size: 0.82em;
  line-height: 1.4;
  opacity: 0.9;
  flex-grow: 1;

  div {
    margin-bottom: 0.25rem;
  }

  strong {
    color: #f1faee;
  }
`;

const CancelledEvent = styled(EventCard)`
  opacity: 0.6;
  background: #34495e;

  ${EventTitle} {
    text-decoration: line-through;
    color: #95a5a6;
  }
`;

// Grid Container
const EventGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  padding: 0.5rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

// ──────────────────────────────────────────────────────────────
// Tab UI components
// ──────────────────────────────────────────────────────────────
const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 1.5rem;
`;

const TabButton = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  font-weight: 600;
  background: ${(p) => (p.active ? '#f0f0f0' : 'transparent')};
  border: none;
  border-bottom: 3px solid ${(p) => (p.active ? '#1976d2' : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;

  &:hover {
    background: #f5f5f5;
  }
`;

// ──────────────────────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────────────────────
export default function Appointments({
  handleWeekendsToggle,
  handleDateSelect,
  handleEventClick,
  addEvent,
  weekendsVisible,
  state,
  appointmentId,
  handleRemove,
  appointmentShow,
  openAppointment,
  modalAppointmentsClose,
  details,
  detailsLoading,
  successDetails,
  errorDetails,

}) {
  const { modalShow, modalClose } = useContext(ModalContext);
  const [activeTab, setActiveTab] = useState('calendar');

  // ── Calendar event rendering ─────────────────────────────────
  function renderEventContent(eventInfo) {
    return (
      <div style={{ padding: '0.6rem', fontSize: '0.85rem', fontWeight: '500' }}>
        {eventInfo.event.extendedProps.status === 'PENDING' ? (
          <i>Calendar: {eventInfo.event.title}</i>
        ) : (
          <i>Subject: {eventInfo.event.title}</i>
        )}
      </div>
    );
  }

  // ── Sidebar event rendering (Grid Card) ──────────────────────
  function renderSidebarEvent(event) {
    const isCancelled = event.status !== 'PENDING';

    const CardComponent = isCancelled ? CancelledEvent : EventCard;

    // console.log("EVENT", event)

    return (
      <CardComponent key={event.id}>
        <EventDate>
          {formatDate(event.start, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </EventDate>

        <EventTitle>
          {isCancelled ? `No ${event.title}` : event.title}
        </EventTitle>

        {!isCancelled && (
          <EventDetails>
            <div>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.5 2H6.75C5.50736 2 4.5 3.00736 4.5 4.25V8.16687H13.5V2Z" fill="#f2f2f2"/>
              <path d="M4.5 9.66687V14.3334H13.5V9.66687H4.5Z" fill="#f2f2f2"/>
              <path d="M4.5 15.8334V19.75C4.5 20.9926 5.50736 22 6.75 22H7.5V22.75C7.5 23.1642 7.83579 23.5 8.25 23.5C8.66421 23.5 9 23.1642 9 22.75V22H13.5V15.8334H4.5Z" fill="#f2f2f2"/>
              <path d="M15 22H17.25C18.4926 22 19.5 20.9926 19.5 19.75V15.8334H15V22Z" fill="#f2f2f2"/>
              <path d="M19.5 14.3334V9.66687H15V14.3334H19.5Z" fill="#f2f2f2"/>
              <path d="M19.5 8.16687V4.25C19.5 3.00736 18.4926 2 17.25 2H15V8.16687H19.5Z" fill="#f2f2f2"/>
            </svg>
             {formatDate(event.start)}
          </div>
            <div>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.25 19.453C20.2421 19.8615 19.9087 20.1895 19.5001 20.1895H4.50013C4.09157 20.1895 3.75818 19.8624 3.75027 19.454L3.75023 19.452L3.75019 19.4493L3.7501 19.4423L3.75 19.4211C3.74999 19.404 3.75015 19.3807 3.75072 19.3516C3.75187 19.2937 3.75468 19.2127 3.76117 19.1119C3.77413 18.9107 3.80189 18.6292 3.86099 18.2937C3.97867 17.6258 4.22374 16.7262 4.73808 15.8194C5.79641 13.9537 7.92408 12.1895 12.0001 12.1895C16.0762 12.1895 18.2038 13.9537 19.2622 15.8194C19.7765 16.7262 20.0216 17.6258 20.1393 18.2937C20.1984 18.6292 20.2261 18.9107 20.2391 19.1119C20.2456 19.2127 20.2484 19.2937 20.2495 19.3516C20.2501 19.3807 20.2503 19.404 20.2503 19.4211L20.2502 19.4423L20.2501 19.4493L20.25 19.452L20.25 19.453Z" fill="#f2f2f2"/>
              <path opacity="0." d="M12.0672 2C9.6678 2 7.72266 3.94514 7.72266 6.34459C7.72266 8.74404 9.6678 10.6892 12.0672 10.6892C14.4667 10.6892 16.4118 8.74404 16.4118 6.34459C16.4118 3.94514 14.4667 2 12.0672 2Z" fill="#f2f2f2"/>
              </svg>
              {event.createdBy}
              </div>
               {event.message && <div>{event.message}</div>}
            <div>
              <strong>
               <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.25 19.453C20.2421 19.8615 19.9087 20.1895 19.5001 20.1895H4.50013C4.09157 20.1895 3.75818 19.8624 3.75027 19.454L3.75023 19.452L3.75019 19.4493L3.7501 19.4423L3.75 19.4211C3.74999 19.404 3.75015 19.3807 3.75072 19.3516C3.75187 19.2937 3.75468 19.2127 3.76117 19.1119C3.77413 18.9107 3.80189 18.6292 3.86099 18.2937C3.97867 17.6258 4.22374 16.7262 4.73808 15.8194C5.79641 13.9537 7.92408 12.1895 12.0001 12.1895C16.0762 12.1895 18.2038 13.9537 19.2622 15.8194C19.7765 16.7262 20.0216 17.6258 20.1393 18.2937C20.1984 18.6292 20.2261 18.9107 20.2391 19.1119C20.2456 19.2127 20.2484 19.2937 20.2495 19.3516C20.2501 19.3807 20.2503 19.404 20.2503 19.4211L20.2502 19.4423L20.2501 19.4493L20.25 19.452L20.25 19.453Z" fill="#323544"/>
              <path opacity="0.4" d="M12.0672 2C9.6678 2 7.72266 3.94514 7.72266 6.34459C7.72266 8.74404 9.6678 10.6892 12.0672 10.6892C14.4667 10.6892 16.4118 8.74404 16.4118 6.34459C16.4118 3.94514 14.4667 2 12.0672 2Z" fill="#323544"/>
              </svg>

                {event.status}
              </strong>
            </div>
          </EventDetails>
        )}
      </CardComponent>
    );
  }

  // ── Sidebar Panel (Grid View) ────────────────────────────────
  const SidebarPanel = (
    <div className="#demo-app-sidebar">
      {/* Weekends Toggle */}
      <div className="demo-app-sidebar-section pb-3">
        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem' }}>
          <input
            type="checkbox"
            checked={weekendsVisible}
            onChange={handleWeekendsToggle}
            style={{ marginRight: '0.5rem' }}
          />
          <span>Toggle Weekends</span>
        </label>
      </div>

      {/* Appointments Grid */}
      <div className="demo-app-sidebar-section">
        <h6
          style={{
            // color: '#f2f2f2',
            textTransform: 'uppercase',
            fontSize: '0.8rem',
            letterSpacing: '0.5px',
            marginBottom: '1rem',
            fontWeight: '600',
          }}
        >
          All Appointments — [{state.length}]
        </h6>

        {state.length === 0 ? (
          <p style={{ color: '#aaa', fontStyle: 'italic', textAlign: 'center', padding: '2rem 0' }}>
            No appointments scheduled.
          </p>
        ) : (
          <EventGrid>{state.map(renderSidebarEvent)}</EventGrid>
        )}
      </div>
    </div>
  );

  // ── Calendar Panel ──────────────────────────────────────────
  const CalendarPanel = (
    <div className="demo-app-main">
      <div className="demo-app-sidebar-section pb-0" style={{ marginLeft: '5.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={weekendsVisible}
            onChange={handleWeekendsToggle}
            style={{ marginRight: '0.5rem' }}
          />
          <span>Toggle Weekends</span>
        </label>
      </div>

      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        headerToolbar={{
          left: 'prev,next,today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        initialView="dayGridMonth"
        allDaySlot={false}
        editable={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={true}
        weekends={weekendsVisible}
        events={state}
        select={handleDateSelect}
        eventContent={renderEventContent}
        eventClick={handleEventClick}
        eventAdd={addEvent}
        eventRemove={handleRemove}
        height="auto"
      />
    </div>
  );

  // ── Render ──────────────────────────────────────────────────
  return (
    <>
      {/* Modals */}
      <Appointment  show={modalShow} onHide={modalClose} />
      <ViewAppointment
        appointmentId={appointmentId}
        show={appointmentShow}
        onHide={modalAppointmentsClose}
        data={details}
        isLoading={detailsLoading}
        isSuccess={successDetails}
        isError={errorDetails}
      />

      {/* Main Container */}
      <div
        style={{
          borderRadius: '12px',
          background: '#ffffff',
          padding: '2rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Tabs */}
        <TabContainer>
          <TabButton
            active={activeTab === 'sidebar'}
            onClick={() => setActiveTab('sidebar')}
          >
            Appointments
          </TabButton>
          <TabButton
            active={activeTab === 'calendar'}
            onClick={() => setActiveTab('calendar')}
          >
            Calendar
          </TabButton>
        </TabContainer>

        {/* Content */}
        <div style={{ minHeight: '500px' }}>
          {activeTab === 'sidebar' ? SidebarPanel : CalendarPanel}
        </div>
      </div>
    </>
  );
}